import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

// Extend the Window interface to include the Google Identity Services
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          prompt: (momentListener?: (notification: any) => void) => void;
          renderButton: (element: HTMLElement, config: any) => void;
          revoke: (email: string, callback: () => void) => void;
        };
      };
    };
  }
}

export interface User {
  id: string;
  email: string;
  name: string;
  picture?: string;
  token: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: () => void;
  logout: () => void;
  isAuthenticated: boolean;
  isAuthorized: boolean; // Specific to allowed domains
  ensureValidToken: () => boolean; // Function to check and refresh token if needed
  promptSuppressed: boolean;
  renderGoogleButton: (element: HTMLElement | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const DEV_AUTH_BYPASS = (process.env.REACT_APP_DEV_AUTH_BYPASS || '0').toString() === '1';
const ALLOWED_DOMAINS = ['hellosimply.com', 'hellosimplyfreelancers.com'];

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [promptSuppressed, setPromptSuppressed] = useState(false);

  const isValidDomain = (email: string): boolean => {
    return ALLOWED_DOMAINS.some(domain => email.endsWith(`@${domain}`));
  };

  const handleCredentialResponse = async (response: any) => {
    try {
      setLoading(true);
      
      // Decode the JWT token to get user info
      const payload = JSON.parse(atob(response.credential.split('.')[1]));
      
      console.log('Google OAuth payload:', payload);
      
      const userInfo: User = {
        id: payload.sub,
        email: payload.email,
        name: payload.name,
        picture: payload.picture,
        token: response.credential,
      };
      
      // Verify the user is from an allowed domain
      const isDomainValid = isValidDomain(userInfo.email);
      console.log('🔍 Domain validation on login:', {
        userEmail: userInfo.email,
        allowedDomains: ALLOWED_DOMAINS,
        isDomainValid: isDomainValid
      });
      
      if (!isDomainValid) {
        const domainsStr = ALLOWED_DOMAINS.map(d => `@${d}`).join(', ');
        throw new Error(`Access restricted to ${domainsStr} organization members only.`);
      }
      
      // Store user info and token
      setUser(userInfo);
      localStorage.setItem('auth_user', JSON.stringify(userInfo));
      localStorage.setItem('auth_token', response.credential);
      
      console.log('✅ User authenticated successfully:', userInfo.email);
      
    } catch (error) {
      console.error('Authentication failed:', error);
      alert(error instanceof Error ? error.message : 'Authentication failed');
      logout();
    } finally {
      setLoading(false);
    }
  };

  const initializeGoogleAuth = () => {
    if (window.google && GOOGLE_CLIENT_ID) {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleCredentialResponse,
        auto_select: true, // Enable automatic sign-in for returning users
        cancel_on_tap_outside: true,
      });
    } else {
      console.error('Google Identity Services not loaded or CLIENT_ID not configured');
      setLoading(false);
    }
  };

  const login = () => {
    if (window.google) {
      window.google.accounts.id.prompt((notification: any) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          console.warn('Google One Tap suppressed, showing fallback button');
          setPromptSuppressed(true);
        }
      });
    } else {
      console.error('Google Identity Services not available');
    }
  };

  const renderGoogleButton = useCallback((element: HTMLElement | null) => {
    if (element && window.google) {
      window.google.accounts.id.renderButton(element, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
      });
    }
  }, []);

  const logout = () => {
    if (user && window.google) {
      window.google.accounts.id.revoke(user.email, () => {
        console.log('Google sign-in revoked');
      });
    }

    setUser(null);
    localStorage.removeItem('auth_user');
    localStorage.removeItem('auth_token');
  };

  // Function to refresh token proactively before expiration
  const refreshTokenIfNeeded = () => {
    const storedToken = localStorage.getItem('auth_token');
    if (!storedToken || !user) return;

    try {
      const tokenPayload = JSON.parse(atob(storedToken.split('.')[1]));
      const currentTime = Date.now() / 1000;
      const timeUntilExpiry = tokenPayload.exp - currentTime;

      // If token expires in less than 15 minutes (900 seconds), refresh it
      if (timeUntilExpiry < 900) {
        console.log('🔄 Token expires soon, refreshing...', { timeUntilExpiry: Math.round(timeUntilExpiry) });
        // Trigger silent re-authentication
        if (window.google) {
          window.google.accounts.id.prompt();
        }
      }
    } catch (error) {
      console.error('Error checking token expiry:', error);
    }
  };

  // Function to check if token is valid and refresh if needed
  const ensureValidToken = () => {
    const storedToken = localStorage.getItem('auth_token');
    if (!storedToken) return false;

    try {
      const tokenPayload = JSON.parse(atob(storedToken.split('.')[1]));
      const currentTime = Date.now() / 1000;

      // If token is already expired, return false
      if (tokenPayload.exp <= currentTime) {
        console.log('❌ Token is expired');
        return false;
      }

      // If token expires in less than 5 minutes, refresh immediately
      if (tokenPayload.exp - currentTime < 300) {
        console.log('⚠️ Token expires very soon, refreshing immediately');
        refreshTokenIfNeeded();
      }

      return true;
    } catch (error) {
      console.error('Error validating token:', error);
      return false;
    }
  };

  // Check for stored auth on component mount
  useEffect(() => {
    // Dev auth bypass: set a mock user and skip Google init
    if (DEV_AUTH_BYPASS) {
      const mock: User = {
        id: 'dev-user',
        email: `dev-user@${ALLOWED_DOMAINS[0]}`,
        name: 'Development User',
        token: ''
      };
      setUser(mock);
      setLoading(false);
      console.log('🟢 Dev auth bypass enabled - mock user set');
      return;
    }
    const checkStoredAuth = () => {
      try {
        const storedUser = localStorage.getItem('auth_user');
        const storedToken = localStorage.getItem('auth_token');
        
        console.log('🔍 Checking stored auth:', { 
          hasStoredUser: !!storedUser, 
          hasStoredToken: !!storedToken 
        });
        
        if (storedUser && storedToken) {
          const userData = JSON.parse(storedUser);
          console.log('🔍 Stored user data:', userData);
          
          // Verify token is still valid (basic check)
          const tokenPayload = JSON.parse(atob(storedToken.split('.')[1]));
          const currentTime = Date.now() / 1000;
          const isTokenValid = tokenPayload.exp > currentTime;
          const isDomainValid = isValidDomain(userData.email);
          
          console.log('🔍 Token validation:', {
            tokenExp: tokenPayload.exp,
            currentTime: currentTime,
            isTokenValid: isTokenValid,
            userEmail: userData.email,
            isDomainValid: isDomainValid,
            allowedDomains: ALLOWED_DOMAINS
          });
          
          // Store validation info to help debug
          if (!isTokenValid || !isDomainValid) {
            localStorage.setItem('debug_auth_failure', JSON.stringify({
              timestamp: new Date().toISOString(),
              reason: !isTokenValid ? 'token_expired' : 'domain_invalid',
              userEmail: userData.email,
              allowedDomains: ALLOWED_DOMAINS,
              tokenExp: tokenPayload.exp,
              currentTime: currentTime
            }));
          }
          
          if (isTokenValid && isDomainValid) {
            setUser(userData);
            console.log('✅ Restored authentication for:', userData.email);
          } else {
            // Token expired or invalid domain
            console.log('❌ Auth invalid - token valid:', isTokenValid, 'domain valid:', isDomainValid);
            logout();
          }
        } else {
          console.log('🔍 No stored auth found');
        }
      } catch (error) {
        console.error('❌ Error checking stored auth:', error);
        logout();
      } finally {
        setLoading(false);
      }
    };

    // Wait for Google script to load, then initialize
    const waitForGoogle = () => {
      if (window.google) {
        initializeGoogleAuth();
        checkStoredAuth();
      } else {
        setTimeout(waitForGoogle, 100);
      }
    };

    waitForGoogle();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set up periodic token refresh checking
  useEffect(() => {
    if (!user) return;

    // Check for token refresh every 2 minutes
    const refreshInterval = setInterval(() => {
      refreshTokenIfNeeded();
    }, 2 * 60 * 1000); // 2 minutes

    return () => clearInterval(refreshInterval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const contextValue: AuthContextType = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    isAuthorized: !!user && isValidDomain(user.email),
    ensureValidToken,
    promptSuppressed,
    renderGoogleButton,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 